import { Delta, Op } from 'quill/core';
import { useCallback } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import deltaToText from '../../editor/utils/deltaToText';
import { gql } from '../../graphql/generated/gql';
import { miscToasts } from '../../toasts/toasts';
import { UIActions } from '../../ui/uiSlice';
import Logger from '../../utils/Logger';
import {
  getFirstLineFromDelta,
  removeFirstLineFromDelta,
} from '../../utils/deltaHelpers';
import useNavigate from '../../utils/router/useNavigate';
import postPath from '../../utils/url/postPath';

const DUPLICATE_POST_FROM_HISTORY_MUTATION = gql(/** @complexity 2 */ `
  mutation duplicatePostFromHistory($title: String!, $content: Json!) {
    post: createPost(title: $title, content: $content) {
      id
      shareSecret
      ...postPath_post
    }
  }
`);

const POST_QUERY = gql(/** @complexity 2 */ `
  query DuplicatePostFromHistoryQuery($postId: ID!, $versionFrom: Int!, $versionTo: Int!) {
    post(id: $postId) {
      id
      title
      historyRange(versionFrom: $versionFrom, versionTo: $versionTo) {
        id
        snapshot
      }
    }
  }
`);

interface DuplicatePostFromHistoryParams {
  postId: string;
  versionTo: number;
}

const getDeltaToStripThreadsAndAnnotations = (delta: Delta) => {
  const change = new Delta();
  delta.ops.forEach((op) => {
    if (op.insert && typeof op.insert === 'object') {
      if (op.insert['table-embed']) {
        const cellsChange: Record<string, { content: Op[] }> = {};
        // @ts-expect-error
        const cells = op.insert['table-embed'].cells || {};
        const identities = Object.keys(cells);
        identities.forEach((identity) => {
          const cellContent = cells[identity]?.content;
          if (cellContent) {
            const cellChange = getDeltaToStripThreadsAndAnnotations(
              new Delta(cellContent),
            );
            if (cellChange.length() > 0) {
              cellsChange[identity] = {
                content: cellChange.ops,
              };
            }
          }
        });

        if (Object.keys(cellsChange).length > 0) {
          change.retain({ 'table-embed': { cells: cellsChange } });
          return;
        }
      } else if (op.insert['code-embed']) {
        const codeChange = getDeltaToStripThreadsAndAnnotations(
          new Delta(op.insert['code-embed'] as Op[]),
        );
        if (codeChange.length() > 0) {
          change.retain({ 'code-embed': codeChange.ops });
          return;
        }
      }
    }

    change.retain(Op.length(op), {
      comment: null,
      reaction: null,
      annotation: null,
    });
  });

  return change.chop();
};

// eslint-disable-next-line import/no-unused-modules -- exported for testing
export const prepareDeltaForCopying = (delta: Delta) => {
  const deltaWithoutFirstLine = removeFirstLineFromDelta(delta);
  return deltaWithoutFirstLine.compose(
    getDeltaToStripThreadsAndAnnotations(deltaWithoutFirstLine),
  );
};

const useDuplicatePostFromHistory = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [duplicatePost] = useMutation(DUPLICATE_POST_FROM_HISTORY_MUTATION);
  const [loadPost] = useLazyQuery(POST_QUERY);

  const duplicate = useCallback(
    async ({ postId, versionTo }: DuplicatePostFromHistoryParams) => {
      try {
        const { data, error } = await loadPost({
          variables: {
            postId,
            versionFrom: versionTo,
            versionTo: versionTo + 1,
          },
        });

        if (error) {
          throw error;
        }

        const post = data?.post;

        if (!post) {
          throw new Error('Post not found');
        }

        const content = new Delta(post.historyRange.snapshot);

        const firstLine = getFirstLineFromDelta(content);
        const title = firstLine ? deltaToText(firstLine) : post.title;

        const response = await duplicatePost({
          variables: {
            title,
            content: JSON.stringify(prepareDeltaForCopying(content).ops),
          },
        });

        const newPost = response.data?.post;
        if (newPost) {
          navigate(
            postPath(newPost, {
              shareSecret: newPost.shareSecret ?? undefined,
            }),
          );
        }
      } catch (error) {
        Logger.error(error);
        dispatch(UIActions.pushToast(miscToasts.genericError));
      }
    },
    [duplicatePost, dispatch, navigate, loadPost],
  );

  return duplicate;
};

export default useDuplicatePostFromHistory;
